/**
 * Navigation Debug Panel
 *
 * Floating overlay that exposes the live navigation history (per-tab) and a
 * scrolling event log. Mounted by `ResizableLayout` only when `navDebug.isPanelEnabled()`.
 * Opt-in: `?navdebug=1`, Cmd+Shift+Alt+N, or `__navDebug.enable()`. Recording is
 * gated the same way - off by default; call sites stay wired for the next sprint.
 * See `frontend/src/lib/nav-debug.ts`.
 *
 * Visual layout:
 *   - Top bar: title + active tab info + clear/close controls
 *   - Section A: Tab Slot Map - one row per tabSlotId showing history stack and pointer
 *   - Section B: Event Log - last N events as a compact table
 */
import { useEffect, useState, useMemo } from "react"
import { X, Trash2, Pause, Play, ChevronDown, ChevronUp } from "lucide-react"
import { Button } from "@/components/ui/button"
import { navDebug, type NavEvent } from "@/lib/nav-debug"
import type { OpenTab } from "@/components/features/document-tabs"
import type { PerTabNavigationApi } from "@/hooks/use-navigation-history"

interface NavigationDebugPanelProps {
  navigation: PerTabNavigationApi
  openTabs: OpenTab[]
  activeTabId: string | null
}

const MAX_VISIBLE_EVENTS = 80

export function NavigationDebugPanel({
  navigation,
  openTabs,
  activeTabId,
}: NavigationDebugPanelProps) {
  const [, forceRender] = useState(0)
  const [paused, setPaused] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [filterSlot, setFilterSlot] = useState<string | "">("")

  useEffect(() => {
    return navDebug.subscribe(() => {
      if (!paused) forceRender((n) => n + 1)
    })
  }, [paused])

  const activeTab = openTabs.find((t) => t.id === activeTabId)
  const activeSlotId = activeTab?.tabSlotId ?? "tab-main"
  const activeSlotState = navigation.tabNavigationState[activeSlotId]
  const activeSlotInHistory = Boolean(activeSlotState)

  const events = useMemo(() => {
    const all = navDebug.getEvents()
    const filtered = filterSlot
      ? all.filter((e) => e.slotId === filterSlot || e.slotId === "*")
      : all
    return filtered.slice(-MAX_VISIBLE_EVENTS).reverse()
  }, // re-compute on every render so live updates show
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [filterSlot, navigation.tabNavigationState])

  const slotIds = Object.keys(navigation.tabNavigationState).sort()
  const hasOrphanSlots = slotIds.some(
    (s) => s !== "tab-main" && !openTabs.some((t) => t.tabSlotId === s),
  )

  return (
    <div
      data-testid="navigation-debug-panel"
      className="fixed bottom-4 right-4 z-[9999] w-[480px] max-h-[70vh] flex flex-col rounded-lg border border-border bg-background shadow-2xl text-xs font-mono"
      style={{ resize: "both", overflow: "hidden" }}
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2 bg-muted/50">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">Nav Debug</span>
          <span className="text-muted-foreground text-[10px]">
            slot=<code className="bg-muted px-1 rounded">{activeSlotId}</code>
            {!activeSlotInHistory && (
              <span className="ml-1 text-destructive font-bold">(no history!)</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            title={paused ? "Resume updates" : "Pause updates"}
            onClick={() => setPaused((p) => !p)}
          >
            {paused ? <Play className="size-3" /> : <Pause className="size-3" />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Clear log"
            onClick={() => navDebug.clear()}
          >
            <Trash2 className="size-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title={collapsed ? "Expand" : "Collapse"}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Close panel (use ?navdebug=1 or window.__navDebug.enable() to reopen)"
            onClick={() => navDebug.disable()}
          >
            <X className="size-3" />
          </Button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="border-b border-border bg-background overflow-auto" style={{ maxHeight: "30vh" }}>
            <div className="px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Tab Slot Map ({slotIds.length})
              </div>
              {slotIds.length === 0 && (
                <div className="text-muted-foreground italic">No history slots yet.</div>
              )}
              {slotIds.map((slotId) => {
                const slot = navigation.tabNavigationState[slotId]!
                const ownerTab = openTabs.find((t) => t.tabSlotId === slotId)
                const isOrphan = !ownerTab && slotId !== "tab-main"
                const isActive = slotId === activeSlotId
                return (
                  <div
                    key={slotId}
                    className={`mb-2 rounded border px-2 py-1 cursor-pointer ${
                      isActive
                        ? "border-primary bg-primary/5"
                        : isOrphan
                          ? "border-destructive/50 bg-destructive/5"
                          : "border-border"
                    }`}
                    onClick={() => setFilterSlot(filterSlot === slotId ? "" : slotId)}
                    title="Click to filter event log to this slot"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold truncate">
                        <code>{slotId}</code>
                        {isActive && <span className="ml-1 text-primary">(active)</span>}
                        {isOrphan && (
                          <span className="ml-1 text-destructive">(orphan)</span>
                        )}
                      </div>
                      <div className="text-muted-foreground text-[10px] shrink-0">
                        idx {slot.currentIndex}/{slot.history.length - 1}
                      </div>
                    </div>
                    {ownerTab && (
                      <div className="text-muted-foreground text-[10px] truncate">
                        owner: {ownerTab.title} ({ownerTab.id})
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {slot.history.map((entry, i) => (
                        <span
                          key={i}
                          className={`text-[10px] px-1 rounded ${
                            i === slot.currentIndex
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}
                          title={JSON.stringify(entry, null, 2)}
                        >
                          {i}:{entry.documentId
                            ? entry.documentId.length > 8
                              ? entry.documentId.slice(0, 8) + "..."
                              : entry.documentId
                            : entry.folderPath ?? entry.viewMode}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
              {hasOrphanSlots && (
                <div className="text-[10px] text-destructive mt-1">
                  Orphan slot = a history slot not owned by any current tab. Likely
                  Bug 1: a `navigate()` call used `documentId` instead of `tabSlotId`.
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-background" style={{ minHeight: "8rem" }}>
            <div className="sticky top-0 bg-muted/50 px-3 py-1 border-b border-border flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Event Log ({events.length}
                {filterSlot ? ` filtered: ${filterSlot}` : ""})
              </span>
              {filterSlot && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-[10px] px-1"
                  onClick={() => setFilterSlot("")}
                >
                  clear filter
                </Button>
              )}
            </div>
            <table className="w-full">
              <thead className="text-[10px] text-muted-foreground sticky top-[26px] bg-background">
                <tr className="border-b border-border">
                  <th className="text-left px-2 py-1 font-normal">#</th>
                  <th className="text-left px-2 py-1 font-normal">action</th>
                  <th className="text-left px-2 py-1 font-normal">slot</th>
                  <th className="text-left px-2 py-1 font-normal">caller</th>
                  <th className="text-left px-2 py-1 font-normal">before?after</th>
                  <th className="text-left px-2 py-1 font-normal">doc / sync</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <EventRow key={e.seq} event={e} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function EventRow({ event }: { event: NavEvent }) {
  const docId = event.entry?.documentId ?? event.result?.documentId ?? null
  const folderPath = event.entry?.folderPath ?? event.result?.folderPath ?? null
  const isFailedGoBack = event.action === "goBack" && !event.result
  const isFailedGoForward = event.action === "goForward" && !event.result
  const isSnapshot = event.action === "snapshot"
  const isUrlSync = event.action === "urlSync"
  const flag = isFailedGoBack || isFailedGoForward
  const urlSyncSkip = isUrlSync && event.urlSync?.outcome === "skip"

  return (
    <tr
      className={`border-b border-border/50 hover:bg-muted/30 ${
        flag ? "bg-destructive/10" : ""
      } ${isSnapshot ? "opacity-50" : ""} ${urlSyncSkip ? "opacity-70" : ""}`}
    >
      <td className="px-2 py-0.5 text-muted-foreground text-[10px]">{event.seq}</td>
      <td className="px-2 py-0.5">
        <ActionBadge action={event.action} />
      </td>
      <td className="px-2 py-0.5">
        <code className="text-[10px]">{event.slotId}</code>
      </td>
      <td className="px-2 py-0.5 text-[10px] text-muted-foreground">
        {event.caller ?? "-"}
      </td>
      <td className="px-2 py-0.5 text-[10px] text-muted-foreground">
        {isSnapshot && event.fullState
          ? `${Object.keys(event.fullState).length} slots`
          : event.stateBefore && event.stateAfter
            ? `${event.stateBefore.index}/${Math.max(0, event.stateBefore.historyLen - 1)} -> ${
                event.stateAfter.index
              }/${Math.max(0, event.stateAfter.historyLen - 1)}`
            : isUrlSync && event.urlSync
              ? event.urlSync.outcome
              : "-"}
      </td>
      <td className="px-2 py-0.5 text-[10px] truncate max-w-[140px]">
        {isUrlSync && event.urlSync ? (
          <span
            title={[
              event.urlSync.pathname,
              event.urlSync.targetUrl,
              event.urlSync.activeTabId,
            ]
              .filter(Boolean)
              .join(" | ")}
          >
            {event.urlSync.reason}
          </span>
        ) : isSnapshot && event.fullState ? (
          <span className="text-muted-foreground" title={JSON.stringify(event.fullState)}>
            sync
          </span>
        ) : docId ? (
          <code title={docId}>
            {docId.length > 16 ? docId.slice(0, 16) + "..." : docId}
          </code>
        ) : folderPath ? (
          <span title={folderPath}>folder {folderPath}</span>
        ) : (
          "-"
        )}
      </td>
    </tr>
  )
}

function ActionBadge({ action }: { action: NavEvent["action"] }) {
  const styles: Record<NavEvent["action"], string> = {
    navigate: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    goBack: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    goForward: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    snapshot: "bg-muted text-muted-foreground",
    urlSync: "bg-violet-500/10 text-violet-700 dark:text-violet-400",
  }
  return (
    <span className={`text-[10px] px-1 py-0.5 rounded ${styles[action]}`}>
      {action}
    </span>
  )
}
